import { useState, useEffect } from "react";
import { Bookmark, BookmarkCheck, Sun, Moon, Type, Minus, Plus } from "lucide-react";
import { isBookmarked, toggleBookmark } from "@/lib/bookmarks";
import { READING_MODES, FONT_FAMILIES, getReadingMode, setReadingMode as saveMode, getFontSize, setFontSize as saveSize, getFontFamily, setFontFamily as saveFamily } from "@/lib/readingMode";
import { toast } from "sonner";

export default function ReadingToolbar({ article, onModeChange, onFontSizeChange, onFontFamilyChange }) {
  const [saved, setSaved] = useState(false);
  const [mode, setMode] = useState(getReadingMode);
  const [fontSize, setFontSize] = useState(getFontSize);
  const [fontFamily, setFontFamily] = useState(getFontFamily);
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    if (article?.slug) setSaved(isBookmarked(article.slug));
  }, [article?.slug]);

  const handleToggleBookmark = () => {
    if (!article) return;
    const { added } = toggleBookmark(article);
    setSaved(added);
    toast(added ? "Article sauvegardé" : "Article retiré des signets");
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    saveMode(newMode);
    onModeChange?.(newMode);
  };

  const handleFontSize = (delta) => {
    const next = Math.max(80, Math.min(140, fontSize + delta));
    setFontSize(next);
    saveSize(next);
    onFontSizeChange?.(next);
  };

  const modeIcons = { default: Sun, sepia: Type, dark: Moon };

  return (
    <div className="flex items-center gap-1" data-testid="reading-toolbar">
      <button
        onClick={handleToggleBookmark}
        className="p-2 rounded-md hover:bg-muted transition-colors"
        title={saved ? "Retirer des signets" : "Ajouter aux signets"}
        data-testid="bookmark-btn"
      >
        {saved ? (
          <BookmarkCheck className="h-5 w-5 text-primary" />
        ) : (
          <Bookmark className="h-5 w-5 text-muted-foreground" />
        )}
      </button>

      <div className="relative">
        <button
          onClick={() => setShowPanel(!showPanel)}
          className="p-2 rounded-md hover:bg-muted transition-colors"
          title="Mode lecture"
          data-testid="reading-mode-btn"
        >
          <Type className="h-5 w-5 text-muted-foreground" />
        </button>

        {showPanel && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)} />
            <div className="absolute right-0 top-full mt-2 z-50 bg-popover border border-border rounded-lg shadow-xl p-4 w-56" data-testid="reading-panel">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">Mode lecture</p>
              <div className="flex gap-2 mb-4">
                {Object.entries(READING_MODES).map(([key, val]) => {
                  const Icon = modeIcons[key];
                  return (
                    <button
                      key={key}
                      onClick={() => handleModeChange(key)}
                      className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-md border transition-colors ${
                        mode === key ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
                      }`}
                      data-testid={`mode-${key}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-xs">{val.label}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Taille du texte</p>
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => handleFontSize(-10)} className="p-1.5 rounded hover:bg-muted" data-testid="font-decrease">
                  <Minus className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium">{fontSize}%</span>
                <button onClick={() => handleFontSize(10)} className="p-1.5 rounded hover:bg-muted" data-testid="font-increase">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Police</p>
              <div className="flex gap-2">
                {Object.entries(FONT_FAMILIES).map(([key, val]) => (
                  <button
                    key={key}
                    onClick={() => { setFontFamily(key); saveFamily(key); onFontFamilyChange?.(key); }}
                    className={`flex-1 p-2 rounded-md border text-center transition-colors ${
                      fontFamily === key ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
                    }`}
                    style={{ fontFamily: val.value }}
                    data-testid={`font-${key}`}
                  >
                    <span className="text-xs">{val.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
